import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from '@cyphex/ui';
import type { CallCredentials } from '@cyphex/types/calls';
import { useDesktopMediaBridge } from '@/features/desktop/hooks/useDesktopMediaBridge';
import { MediaSettingsModal } from '@/features/meeting/components/settings/MediaSettingsModal';
import { PresenterBar } from '@/features/meeting/components/PresenterBar';
import { ScreenSharePicker } from '@/features/meeting/components/ScreenSharePicker';
import { TakeOverDialog } from '@/features/meeting/components/TakeOverDialog';
import { useShareController } from '@/features/meeting/hooks/useShareController';
import { useCallActions } from '../../hooks/useCallActions';
import { useCallParties } from '../../hooks/useCallParties';
import { useCallRoom } from '../../hooks/useCallRoom';
import { useConversation } from '@/features/chat/hooks/useConversation';
import { CallChatPanel } from './CallChatPanel';
import { CallControls } from './CallControls';
import { CallHeader } from './CallHeader';
import { CallMoreMenu } from './CallMoreMenu';
import { CallStage } from './CallStage';

export interface CallRoomProps {
  callId: string;
  credentials: CallCredentials;
  /** Fallback name until the roster lands (the handoff already knows one). */
  peerName: string;
  withVideo: boolean;
  onHold: boolean;
  /**
   * The user has walked away and the surface is hidden, not unmounted, so the
   * media session survives. Anything that is a SIGNAL rather than a picture —
   * the chat rail's read receipts and typing — has to stop while this is true.
   */
  compact: boolean;
  onToggleHold: () => void;
  onHangup: () => void;
  /** The consult bar, when an attended transfer is running on this call. */
  banner?: ReactNode;
  /**
   * Painted over the VIDEO STAGE — and only the stage. The ringing screen is
   * the one user of it: it must hide the empty room the caller is publishing
   * into without hiding the header bar, which carries the call toolbar and
   * therefore the Chat button, for the whole length of an outgoing ring.
   */
  stageOverlay?: ReactNode;
}

/**
 * ONE status line — MY side of the call only.
 *
 * Being held by somebody else is said once, by the badge in the header, and
 * deliberately not repeated here: a status line and a badge carrying the same
 * words are two things to keep in sync and two things a screen reader reads
 * out. My own hold belongs on this line because it is the thing I chose and the
 * thing I can undo.
 */
const statusLine = (onHold: boolean, ringing: boolean, live: boolean): string => {
  if (onHold) return 'On hold';
  if (ringing) return 'Ringing…';
  return live ? 'Connected' : 'Connecting…';
};

/** The live call: header, video stage, toolbar. Media comes from the SFU. */
export function CallRoom(props: CallRoomProps) {
  const room = useCallRoom(props.callId, props.credentials, props.withVideo);
  /* WHO IS ACTUALLY ON THE CALL, from the API roster rather than the one name
     the handoff carried. The SFU knows who is publishing; only this knows who
     is being rung, who has left, and who has put whom on hold. */
  const parties = useCallParties(props.callId);
  const actions = useCallActions(props.callId);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  /* Kept here rather than inside the rail so that walking away and coming back
     — which hides this tree without unmounting it — returns to the call with
     chat still open, the way leaving a Teams call window does. */
  const [chatOpen, setChatOpen] = useState(false);


  const { client, session, local } = room;
  const snapshot = session.snapshot;
  /* ONE PRESENTER, and the take-over — the same controller the meeting room
     uses, because a call and a meeting differ in how they START, not in what
     "somebody else is presenting" means. */
  const share = useShareController(client, snapshot);
  const live = snapshot.state === 'connected';

  /*
   * GLOBAL PUSH-TO-TALK AND MUTE REACH THIS CALL.
   *
   * A meeting registered here and a call never did, so the single most-requested
   * reason people install a desktop client — mute yourself with your hands in
   * another application — silently did nothing during a call. Once the call
   * moved into a window of its own that became actively wrong rather than
   * merely missing: with no binding in this document, a hotkey pressed during a
   * call fell through to whatever media the WORKSPACE happened to hold, so
   * muting a call muted a voice channel in the other window instead.
   *
   * With this registered, main/mediaRouter.ts has something to route to, and a
   * window reporting a live CALL outranks the workspace for every media hotkey.
   */
  useDesktopMediaBridge(client, local?.micOn ?? false);

  /*
   * THE UNREAD COUNT ON THE CHAT BUTTON.
   *
   * Gated on `live` on purpose: this is a GET, and firing it during the RING
   * would put a request on the one path where latency is felt. Nobody can have
   * unread call-chat before the call connects anyway.
   *
   * Zeroed while the rail is open rather than unsubscribed, because the panel
   * marks the thread read as you look at it — leaving the badge lit over an
   * open, already-read conversation is how a counter loses people's trust.
   */
  const chatConversation = useConversation(live ? parties.conversationId : null);
  const chatUnread = chatOpen ? 0 : (chatConversation.conversation?.self.unreadCount ?? 0);

  /* The clock starts when MEDIA connects, not when the route mounted — a slow
     ICE handshake must not be billed to the conversation. */
  useEffect(() => {
    if (live) setConnectedAt((current) => current ?? Date.now());
  }, [live]);

  const micTrack = client.getLocalMicTrack();
  const micStream = useMemo(() => (micTrack ? new MediaStream([micTrack]) : null), [micTrack]);

  const onToggleChat = useCallback(() => setChatOpen((open) => !open), []);
  const onCloseChat = useCallback(() => setChatOpen(false), []);

  const heldByNames = parties.heldBy.map((party) => party.user?.displayName ?? 'Someone');
  /* The roster is authoritative once it lands; the handoff's single name is the
     stand-in for the first few hundred milliseconds after a call is placed. */
  const title = parties.counterparts.length === 0 ? props.peerName : parties.label;

  return (
    <div className="flex h-full flex-col bg-stage">
      <CallHeader
        title={title}
        avatarName={parties.counterparts[0]?.user?.displayName ?? props.peerName}
        /* The CALL's roster, not the SFU room's. A group call whose members
           answered on another device showed "1" here, which was never the
           number of people on the call. */
        participantCount={Math.max(parties.liveCount, snapshot.participants.length)}
        connectedAt={connectedAt}
        onHold={props.onHold}
        heldByNames={heldByNames}
        status={statusLine(props.onHold, parties.ringing, live)}
        /* ONE BAR. The toolbar used to sit along the bottom, spending a
           second ~100px strip of the window on chrome. Every pixel of it
           came off the shared screen, which is bounded by the stage's
           HEIGHT — so folding the controls into this bar makes the share
           both taller and, at a fixed aspect ratio, wider. */
        actions={
        <CallControls
          micOn={local?.micOn ?? false}
          cameraOn={local?.cameraOn ?? false}
          /* Truthful now: with one presenter enforced, "active" means the
             other side can actually see this screen. */
          sharing={share.isLocalPresenter}
          onHold={props.onHold}
          chatOpen={chatOpen}
          chatUnread={chatUnread}
          disabled={!live}
          /*
           * THESE MUST CATCH, and it is not defensive boilerplate.
           *
           * `setCameraEnabled` REJECTS when the camera cannot be opened — the
           * permission was refused, another app holds the device, or there is no
           * camera at all — and its own comment says "a permission denial must
           * reach the caller's toast, but must not leave the client believing it
           * has a camera to publish". `void` threw that away: the rejection
           * became an unhandled promise in the console, `cameraWanted` was reset
           * to false inside, and the button simply did nothing, for ever, with no
           * error anywhere a user could see it. "The video button doesn't work"
           * is what that looks like from the outside.
           *
           * useMeetingActions has always caught these; only the call toolbar
           * dropped them, so the same click behaved differently in a meeting and
           * in a call.
           */
          onToggleMic={() =>
            void client
              .setMicEnabled(!(local?.micOn ?? false))
              .catch(() => toast.error('Could not switch the microphone.'))
          }
          onToggleCamera={() =>
            void client
              .setCameraEnabled(!(local?.cameraOn ?? false))
              .catch(() => toast.error('Could not start the camera. Check the browser permission.'))
          }
          onToggleShare={share.toggle}
          onToggleHold={props.onToggleHold}
          onToggleChat={onToggleChat}
          onOpenDevices={() => setDevicesOpen(true)}
          onHangup={props.onHangup}
          moreMenu={
            <CallMoreMenu
              callId={props.callId}
              actions={actions}
              presentUserIds={parties.counterparts.flatMap((party) =>
                party.user === null ? [] : [party.user.id],
              )}
              disabled={!live || actions.isBusy}
              /* NOT `live`. That is the caller's own MEDIA state, and the caller
                 publishes into an empty room from the first second — so `live`
                 is true for the whole ring and "Move to meeting" was clickable
                 the entire time somebody's phone was ringing. The roster is the
                 only thing that knows whether anybody answered. */
              canEscalate={parties.answered}
            />
          }
        />
        }
      />

      {props.banner}

      {/* Stage and rail share one row so opening chat SHRINKS the video rather
          than covering it — the same arrangement the meeting uses. */}
      <div className="flex min-h-0 flex-1">
        {/* The overlay's positioning context. Rendered ALWAYS, never only while
            something is overlaid: a wrapper that appeared when the ring started
            would re-parent the stage mid-call and take every <video> element
            with it. Constant structure, conditional leaf. */}
        <div className="relative flex min-h-0 flex-1">
          <CallStage
            client={client}
            plan={room.plan}
            layout={room.layout}
            shareTile={room.shareTile}
            pinnedId={room.pinnedId}
            state={snapshot.state}
            presenterBar={
              share.presenter === null ? null : (
                <PresenterBar
                  presenter={share.presenter}
                  isLocalPresenter={share.isLocalPresenter}
                  withAudio={share.withAudio}
                  soundRequested={share.soundRequested}
                  onTakeOver={share.requestTakeOver}
                  onStop={share.stop}
                />
              )
            }
            totalParticipants={snapshot.participants.length}
            stageRef={room.stageRef}
            onPin={room.setPinned}
            onPageChange={room.setPage}
          />

          {/* Over the picture, under the toolbar — and beside the rail, which
              is a sibling of this box rather than a child, so chat opened
              during a ring is readable next to the ringing screen. */}
          {props.stageOverlay === undefined || props.stageOverlay === null ? null : (
            <div className="absolute inset-0 z-10">{props.stageOverlay}</div>
          )}
        </div>

        {/* Unmounted, not hidden, when the surface goes compact: a rail nobody
            can see must not mark the thread read or broadcast that I am typing.
            Remounting costs nothing — all of its state is the query cache, and
            which thread this call resolved to is latched by call id, so coming
            back lands on the same conversation rather than resolving a second
            one against a roster that moved while the rail was gone. */}
        {chatOpen && !props.compact ? (
          <CallChatPanel
            callId={props.callId}
            linkedConversationId={parties.conversationId}
            rosterUserIds={parties.rosterUserIds}
            /* WHO the thread is with comes from the roster, so a roster that
               never landed is a rail that can never resolve one. Handing the
               failure down is what turns "skeletons for the rest of the call"
               into something a user can see and press. */
            rosterError={parties.isError}
            onRetryRoster={parties.refetch}
            onClose={onCloseChat}
          />
        ) : null}
      </div>

      <ScreenSharePicker
        open={share.pickerOpen}
        onOpenChange={share.setPickerOpen}
        onStart={share.start}
      />

      <TakeOverDialog
        presenter={share.blockedBy}
        onConfirm={share.confirmTakeOver}
        onCancel={share.cancelTakeOver}
      />

      <MediaSettingsModal
        open={devicesOpen}
        onOpenChange={setDevicesOpen}
        preferences={room.media.preferences}
        api={room.media}
        processing={snapshot.processing}
        quality={snapshot.quality}
        profile={snapshot.profile}
        downlinkBps={snapshot.downlinkBps}
        packetLoss={snapshot.packetLoss}
        degradedReason={null}
        micStream={micStream}
      />
    </div>
  );
}
